import React, { useEffect, useState } from 'react';
import { DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { DossierApiApiDossiersIdDissocierPatchRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { DocumentsSelectorsForm } from '@europrocurement/l2d-modules';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import { castIriToId } from '@europrocurement/l2d-utils';
import { useModal } from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';
import { useHandleError } from '@europrocurement/l2d-hooks';

export type DissociateFormalitiesFormProps = {
    formalitiesIds: number[];
    dossier: DossierOpenSearch;
    documents: DisplayedDocument[];
    onSubmitDone?: () => void;
};
const DissociateFormalitiesForm: React.FunctionComponent<DissociateFormalitiesFormProps> =
    function ({ formalitiesIds, dossier, documents, onSubmitDone = () => {} }) {
        const { formaliteApi, dossierApi } = useFormaliteService();
        const { palette } = useTheme();
        const { modalActions } = useModal();
        const { enqueueSnackbar } = useSnackbar();
        const { apiErrorMessageSnackbar } = useHandleError();
        const [documentsIdsFormality, setDocumentsIdsFormality] = useState<number[]>([]);
        const [selectedDocuments, setSelectedDocuments] = useState<DisplayedDocument[]>([]);
        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

        // hydrate initals states
        useEffect(() => {
            if (!dossier.id) {
                return;
            }

            setDocumentsIdsFormality([]);
            // get formalities from ids props and retrieve documents linked
            formalitiesIds.map((formalityId) => {
                formaliteApi
                    .apiFormalitesIdGet({ id: formalityId.toString() })
                    .then((res) => {
                        const documentsIds = res.data.documents.map((document) =>
                            castIriToId(document),
                        );

                        setDocumentsIdsFormality((state) => state.concat(documentsIds));
                    })
                    .catch((err) => {
                        enqueueSnackbar(
                            'Une erreur est survenue lors de la récupération des documents liés à la prestation',
                            { variant: 'error' },
                        );
                        console.log(err.message);
                    });

                return true;
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleDissociate = () => {
            // To check: validator ongoing ?
            if (!dossier.id) {
                return;
            }
            setIsSubmitting(true);

            const prestationsIds: number[] = [];
            dossier.formalites.map((formality) => {
                if (formalitiesIds.includes(formality.id)) {
                    formality.prestationId.map((prestationId) => prestationsIds.push(prestationId));
                }
                return true;
            });

            const documentsIds = selectedDocuments.map((doc) => doc.id);

            const request: DossierApiApiDossiersIdDissocierPatchRequest = {
                id: dossier.id.toString(),
                apiDossiersIdDissocierPatchRequest: {
                    documentsIds,
                    prestationsIds,
                },
            };
            dossierApi
                .apiDossiersIdDissocierPatch(request)
                .then(() => {
                    enqueueSnackbar('Le dossier à bien été dissocié');
                    modalActions.close();
                })
                .catch((err) => {
                    apiErrorMessageSnackbar(
                        err,
                        'Une erreur est survenue lors de la dissociation du dossier',
                    );
                    console.log(err.message);
                })
                .finally(() => {
                    setIsSubmitting(false);
                    onSubmitDone();
                });
        };

        return (
            <Box sx={{ width: '550px', gap: '24px', display: 'grid' }}>
                <Typography variant="l2dh6">Déplacer dans un nouveau dossier</Typography>

                {documentsIdsFormality && (
                    <Box>
                        <Typography variant="l2dsubtitles">Documents à dupliquer</Typography>

                        <DocumentsSelectorsForm
                            documents={documents}
                            preSelectedDocumentsIds={documentsIdsFormality}
                            setSelectedDocuments={setSelectedDocuments}
                        />
                    </Box>
                )}
                {/* Footer */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'end', gap: '16px' }}>
                        <Button
                            variant="text"
                            sx={{
                                padding: '16px 24px',
                                alignItems: 'center',
                                color: palette.primary.main,
                            }}
                            onClick={() => modalActions.close()}
                        >
                            Annuler
                        </Button>
                        <Button
                            disabled={isSubmitting}
                            onClick={handleDissociate}
                            sx={{
                                padding: '8px 20px',
                                fontSize: '16px',
                                background: palette.primary.main,
                                ':hover': {
                                    background: palette.primary.light,
                                },
                                transition: 'all ease-in-out 300ms',
                            }}
                            variant="contained"
                            key="publish-bodacc-btn"
                        >
                            {isSubmitting ? (
                                <CircularProgress
                                    color="inherit"
                                    size="16px"
                                />
                            ) : (
                                'Créer un nouveau dossier'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

export default DissociateFormalitiesForm;
